"use client"

import React, { useCallback, useEffect, useState } from "react"
import { motion } from "framer-motion"
import { X, Car, Printer, CheckCircle, AlertTriangle, Tags, RotateCcw } from "lucide-react"
import {
  getTiposTarifaBySucursal,
  getSucursalTicketInfo,
  crearTicketIngreso,
  validarMembresia,
  crearIngresoMembresia,
  getHentradaServidor,
  getFechaIngresoMembresiaServidor,
} from "../services/controlService"
import { imprimirTicketIngreso, normalizarPlaca, sellarFechaHora } from "../utils/ticketPrint"
import "./IngresoModal.css"

/** Mínimo de caracteres de una placa, igual que en la app móvil. */
const LARGO_MINIMO_PLACA = 5

const IngresoModal = ({ sucursalId, onClose, onSuccess }) => {
  const [status, setStatus] = useState("loading") // loading | form | processing | success | error
  const [placa, setPlaca] = useState("")
  const [tiposTarifa, setTiposTarifa] = useState([])
  const [tarifa, setTarifa] = useState(null)
  const [sucursal, setSucursal] = useState(null)
  const [error, setError] = useState("")
  const [membresiaCaducada, setMembresiaCaducada] = useState(null)
  const [resultado, setResultado] = useState(null) // { placa, fecha, hora, viaMembresia, cliente }

  const cargar = useCallback(async () => {
    try {
      setStatus("loading")
      setError("")
      const [tipos, info] = await Promise.all([
        getTiposTarifaBySucursal(sucursalId),
        getSucursalTicketInfo(sucursalId),
      ])
      setTiposTarifa(tipos)
      setSucursal(info)
      // Misma heurística que el móvil (selectTarifaByTime): antes de las
      // 18h se preselecciona la primera tarifa; después se elige a mano.
      setTarifa(new Date().getHours() < 18 ? tipos[0] ?? null : null)
      setStatus("form")
    } catch (e) {
      setError(e.message || "No se pudieron cargar las tarifas de la sucursal.")
      setStatus("error")
    }
  }, [sucursalId])

  useEffect(() => {
    cargar()
  }, [cargar])

  /**
   * Imprime y guarda lo impreso para poder reimprimir si sale mal el papel.
   * El ingreso ya quedó registrado cuando esto corre, así que un fallo de
   * impresión se registra en consola pero no se trata como error del alta.
   */
  const imprimir = async (datos) => {
    setResultado(datos)
    try {
      await imprimirTicketIngreso({
        sucursal,
        placa: datos.placa,
        fecha: datos.fecha,
        hora: datos.hora,
      })
    } catch (e) {
      console.error("No se pudo imprimir el ticket de ingreso:", e)
    }
  }

  const registrar = async ({ forzarTicketNormal = false } = {}) => {
    if (placa.length < LARGO_MINIMO_PLACA) {
      setError("Placa inválida.")
      return
    }
    if (!tarifa) {
      setError("Seleccione el tipo de tarifa.")
      return
    }

    setError("")
    setMembresiaCaducada(null)
    setStatus("processing")

    try {
      // 1) ¿Tiene membresía? Mismo orden que el móvil: primero se valida,
      //    y solo si no hay membresía (o el operador lo confirma) se
      //    genera un ticket normal cobrable.
      if (!forzarTicketNormal) {
        const validacion = await validarMembresia(placa, sucursalId)

        if (validacion?.valida === true) {
          if (!validacion.idMembresia) {
            throw new Error("La membresía es válida pero el servidor no devolvió su id.")
          }
          await crearIngresoMembresia(validacion.idMembresia)
          const fechaIngreso = await getFechaIngresoMembresiaServidor(placa, sucursalId)
          const sello = sellarFechaHora(fechaIngreso)
          setStatus("success")
          onSuccess?.()
          await imprimir({
            placa,
            ...sello,
            viaMembresia: true,
            cliente: validacion.cliente || null,
          })
          return
        }

        if (validacion?.valida === false) {
          // Membresía caducada: se pregunta antes de cobrar como ticket normal.
          setMembresiaCaducada(validacion)
          setStatus("form")
          return
        }
      }

      // 2) Ticket normal.
      await crearTicketIngreso({ placa, idTipoTarifa: tarifa.id })

      // La hora oficial la pone la BD; se relee para imprimir la misma que
      // quedó guardada. Si no se puede, se usa la hora local del equipo.
      const hentrada = await getHentradaServidor(placa, sucursalId)
      const sello = sellarFechaHora(hentrada)

      setStatus("success")
      onSuccess?.()
      await imprimir({ placa, ...sello, viaMembresia: false })
    } catch (e) {
      setError(
        e.status === 500
          ? `${e.message} · Revise si el vehículo ya está registrado.`
          : e.message || "No se pudo registrar el ingreso."
      )
      setStatus("error")
    }
  }

  const reimprimir = () => {
    if (!resultado) return
    imprimirTicketIngreso({
      sucursal,
      placa: resultado.placa,
      fecha: resultado.fecha,
      hora: resultado.hora,
    })
  }

  const procesando = status === "processing"

  return (
    <div className="ig-overlay" onClick={onClose}>
      <motion.div
        className="ig-modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 26 }}
      >
        <div className="ig-header">
          <h3>
            <Car size={20} /> Registrar ingreso
          </h3>
          <button className="ig-icon-btn" onClick={onClose} aria-label="Cerrar">
            <X size={20} />
          </button>
        </div>

        {status === "loading" && (
          <div className="ig-state">
            <div className="ig-spinner" /> Cargando tarifas…
          </div>
        )}

        {status === "error" && (
          <div className="ig-body">
            <div className="ig-error">
              <AlertTriangle size={18} /> {error}
            </div>
            <div className="ig-actions">
              <button className="ig-btn ig-btn-ghost" onClick={onClose}>
                Cerrar
              </button>
              <button
                className="ig-btn ig-btn-primary"
                onClick={() => (tiposTarifa.length ? setStatus("form") : cargar())}
              >
                Reintentar
              </button>
            </div>
          </div>
        )}

        {(status === "form" || procesando) && (
          <div className="ig-body">
            <label className="ig-label" htmlFor="ig-placa">
              Placa del vehículo
            </label>
            <input
              id="ig-placa"
              className="ig-placa-input"
              value={placa}
              onChange={(e) => setPlaca(normalizarPlaca(e.target.value))}
              onKeyDown={(e) => e.key === "Enter" && !procesando && registrar()}
              placeholder="ABC1234"
              maxLength={10}
              autoFocus
              disabled={procesando}
              autoComplete="off"
              spellCheck={false}
            />
            <p className="ig-hint">Sin guion ni espacios: así se guarda en la app móvil y en el backend.</p>

            <div className="ig-label ig-label-tarifas">
              <Tags size={14} /> Tipo de tarifa
            </div>
            {tiposTarifa.length === 0 ? (
              <div className="ig-error">
                <AlertTriangle size={18} /> Esta sucursal no tiene tipos de tarifa configurados.
              </div>
            ) : (
              <div className="ig-tarifas">
                {tiposTarifa.map((t) => (
                  <button
                    key={t.id}
                    className={`ig-tarifa ${tarifa?.id === t.id ? "active" : ""}`}
                    onClick={() => setTarifa(t)}
                    disabled={procesando}
                  >
                    {t.nombre}
                  </button>
                ))}
              </div>
            )}

            {membresiaCaducada && (
              <div className="ig-aviso">
                <AlertTriangle size={18} />
                <div>
                  <strong>Membresía caducada</strong>
                  <p>
                    {membresiaCaducada.cliente ? `${membresiaCaducada.cliente}: ` : ""}
                    la membresía de esta placa ya no está vigente
                    {membresiaCaducada.fechaFin ? ` (venció el ${membresiaCaducada.fechaFin})` : ""}.
                    ¿Generar un ticket normal cobrable?
                  </p>
                  <button className="ig-btn ig-btn-primary ig-btn-inline" onClick={() => registrar({ forzarTicketNormal: true })}>
                    Sí, generar ticket normal
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="ig-error">
                <AlertTriangle size={18} /> {error}
              </div>
            )}

            <div className="ig-actions">
              <button className="ig-btn ig-btn-ghost" onClick={onClose} disabled={procesando}>
                Cancelar
              </button>
              <button
                className="ig-btn ig-btn-primary"
                onClick={() => registrar()}
                disabled={procesando || placa.length < LARGO_MINIMO_PLACA || !tarifa}
              >
                {procesando ? (
                  <>
                    <span className="ig-mini-spinner" /> Registrando…
                  </>
                ) : (
                  <>
                    <Printer size={17} /> Registrar e imprimir
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {status === "success" && resultado && (
          <div className="ig-body">
            <div className="ig-success">
              <CheckCircle size={56} />
              <p className="ig-success-title">
                {resultado.viaMembresia ? "Ingreso por membresía registrado" : "Ingreso registrado"}
              </p>
              <p className="ig-success-detalle">
                {resultado.placa} · {resultado.hora}
                {resultado.cliente ? ` · ${resultado.cliente}` : ""}
              </p>
            </div>
            <div className="ig-actions">
              <button className="ig-btn ig-btn-ghost" onClick={reimprimir}>
                <RotateCcw size={16} /> Reimprimir
              </button>
              <button className="ig-btn ig-btn-primary" onClick={onClose} autoFocus>
                Listo
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default IngresoModal
